import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, Disc3, ArrowLeft, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import Countdown from "@/components/Countdown";
import TicketBooking from "@/components/TicketBooking";
import { BRAND } from "@/lib/content";
import api from "@/lib/api";

export default function EventDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setEv(null); setNotFound(false);
    api.get(`/events/${slug}`).then((r) => setEv(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-neutral-500">Event not found.</p>
      <Link to="/events" className="btn-ghost">Back to Events</Link>
    </div>
  );
  if (!ev) return <div className="min-h-screen flex items-center justify-center text-neutral-400">Loading…</div>;

  const fmt = new Date(ev.date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "America/New_York" });

  return (
    <>
      <Seo title={ev.title} description={ev.description} path={`/events/${ev.slug}`} image={ev.flyer_image} />
      <div className="pt-28 md:pt-32 pb-20">
        <div className="max-w-[1300px] mx-auto px-5 md:px-8">
          <Link to="/events" data-testid="back-to-events" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-6 text-sm"><ArrowLeft size={16} /> All Events</Link>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="rounded-[2rem] overflow-hidden">
                <img src={ev.flyer_image} alt={ev.title} className="w-full h-auto object-cover" />
              </motion.div>
              <h1 className="display-mega text-4xl md:text-6xl mt-8">{ev.title}</h1>
              {ev.tagline && <p className="text-neutral-500 text-lg mt-2">{ev.tagline}</p>}
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-neutral-600 text-sm">
                <span className="flex items-center gap-2"><Calendar size={16} /> {fmt}</span>
                {ev.start_time && <span className="flex items-center gap-2"><Clock size={16} /> {ev.start_time}</span>}
                {ev.dj && <span className="flex items-center gap-2"><Disc3 size={16} /> {ev.dj}</span>}
                <span className="flex items-center gap-2"><MapPin size={16} /> {BRAND.addressShort}</span>
              </div>
              <p className="text-neutral-600 text-lg leading-relaxed mt-6">{ev.description}</p>
            </div>

            <div className="lg:sticky lg:top-28">
              <div className="card-lux p-6 md:p-7">
                {ev.status === "upcoming" && (
                  <div className="mb-6 pb-6 border-b border-black/10">
                    <span className="overline">Doors open in</span>
                    <div className="mt-3"><Countdown date={ev.date} /></div>
                  </div>
                )}
                <h2 className="display-mega text-2xl mb-1">Get in</h2>
                <p className="text-neutral-500 text-sm mb-5">{ev.cover_charge || "Reserve your spot"}</p>
                <TicketBooking ev={ev} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
